import { useMemo, useState } from 'react';
import type { ApiClient } from '../../api';
import type { MsrEinstellwertvorlage } from './typen';
import {
  ContentSection, DataTable, Drawer, EmptyState, ErrorState, Feld, FilterBar,
  FormSection, StatusBadge, type FilterDefinition, type FilterWerte, type Spalte,
} from '../../muster';

/**
 * Unterkarte „Einstellwertvorlagen" im Reiter Grundlagen (Spezifikation 14.1)
 * — gruppiert nach Aggregatkürzel bzw. Anlagenart, Filter über die Nutzung.
 * Wie bei den Datenpunktvorlagen wirkt eine Änderung erst beim nächsten
 * Ableitungslauf; von Hand gepflegte Einstellwerte einzelner Anlagen
 * (`Herkunft` 'gepflegt') werden dabei nie überschrieben.
 *
 * Umzug U6c: Liste (`DataTable`) und Bearbeitung (`Drawer`) getrennt, Filter
 * auf `FilterBar`. Min/Max/Schrittweite tragen jetzt ihre Einheit am Feld
 * statt gar nicht (Auftrag Abschnitt 15).
 */
export function Einstellwertvorlagen({ api, projektId, vorlagen, onGeaendert }: {
  api: ApiClient; projektId: string; vorlagen: MsrEinstellwertvorlage[];
  onGeaendert: () => void;
}) {
  const [werte, setWerte] = useState<FilterWerte>({ nutzung: '' });
  const [entwurf, setEntwurf] = useState<MsrEinstellwertvorlage | null>(null);
  const [speichertLaeuft, setSpeichertLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const nutzungen = useMemo(() => Array.from(
    new Set(vorlagen.map((v) => v.nutzung).filter((n): n is string => !!n)),
  ).sort(), [vorlagen]);

  const filter: FilterDefinition[] = useMemo(() => [{
    schluessel: 'nutzung', beschriftung: 'Nutzung', art: 'auswahl',
    hinweis: 'Vorlagen ohne Nutzung gelten immer und bleiben stehen.',
    optionen: nutzungen.map((n) => ({ wert: n, beschriftung: n })),
  }], [nutzungen]);

  const gefiltert = werte.nutzung
    ? vorlagen.filter((v) => !v.nutzung || v.nutzung === werte.nutzung)
    : vorlagen;

  const gruppen = useMemo(() => {
    const m = new Map<string, MsrEinstellwertvorlage[]>();
    for (const v of gefiltert) {
      const schluessel = v.aggregatKuerzel ?? v.anlagenart ?? '—';
      const liste = m.get(schluessel) ?? [];
      liste.push(v);
      m.set(schluessel, liste);
    }
    return Array.from(m.entries()).sort(([a], [b]) => a.localeCompare(b, 'de'));
  }, [gefiltert]);

  const bearbeiten = (v: MsrEinstellwertvorlage) => { setEntwurf({ ...v }); setFehler(null); };
  const aendern = (teil: Partial<MsrEinstellwertvorlage>) =>
    setEntwurf((v) => (v ? { ...v, ...teil } : v));

  const speichern = async () => {
    if (!entwurf) return;
    setSpeichertLaeuft(true);
    setFehler(null);
    try {
      const { id, ...rest } = entwurf;
      await api.msrEinstellwertvorlageSetzen(projektId, id, rest);
      setEntwurf(null);
      onGeaendert();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setSpeichertLaeuft(false);
    }
  };

  const spalten: Spalte<MsrEinstellwertvorlage>[] = [
    {
      schluessel: 'suffix', titel: 'Suffix', immerSichtbar: true, wert: (v) => v.suffix,
      inhalt: (v) => <span style={{ fontFamily: 'monospace' }}>{v.suffix}</span>,
    },
    { schluessel: 'nutzung', titel: 'Nutzung', wert: (v) => v.nutzung ?? 'alle' },
    { schluessel: 'bezeichnung', titel: 'Bezeichnung', wert: (v) => v.bezeichnung },
    { schluessel: 'wert', titel: 'Wert', wert: (v) => v.wert },
    { schluessel: 'einheit', titel: 'Einheit', wert: (v) => v.einheit ?? null },
    { schluessel: 'min', titel: 'Min', art: 'zahl', wert: (v) => v.min ?? null },
    { schluessel: 'max', titel: 'Max', art: 'zahl', wert: (v) => v.max ?? null },
    { schluessel: 'schrittweite', titel: 'Schrittweite', art: 'zahl',
      wert: (v) => v.schrittweite ?? null },
    { schluessel: 'quelle', titel: 'Quelle', wert: (v) => v.quelle ?? null },
    { schluessel: 'begruendung', titel: 'Begründung', wert: (v) => v.begruendung ?? null },
    {
      schluessel: 'istAktiv', titel: 'Zustand', art: 'status', breite: '120px',
      wert: (v) => (v.istAktiv ? 'aktiv' : 'stillgelegt'),
      inhalt: (v) => (v.istAktiv
        ? <StatusBadge klein zustand="gut" text="aktiv" />
        : <StatusBadge klein zustand="neutral" text="stillgelegt" />),
    },
    {
      schluessel: 'geltung', titel: 'Geltung', breite: '170px',
      wert: (v) => (v.projektbezogen ? 'Projekt' : 'global'),
      inhalt: (v) => (
        <>
          {!v.projektbezogen && (
            <StatusBadge klein zustand="info" text="global"
              titel="Gilt für alle Projekte." />
          )}
          <button type="button" className="m-knopf" style={{ marginLeft: 6 }}
            onClick={() => bearbeiten(v)}>Bearbeiten</button>
        </>
      ),
    },
  ];

  return (
    <ContentSection
      titel="Einstellwertvorlagen"
      beschreibung={'Vorlagenänderungen wirken beim nächsten Ableitungslauf; gepflegte '
        + 'Werte je Anlage bleiben unberührt.'}
      meta={`${gruppen.length} Gruppe(n) · ${vorlagen.length} Vorlagen`}
    >
      <FilterBar filter={filter} werte={werte} beiAenderung={setWerte} inLeiste={1} />

      {gruppen.map(([schluessel, liste]) => (
        <DataTable
          key={schluessel}
          name={schluessel}
          spalten={spalten}
          zeilen={liste}
          schluessel={(v) => v.id}
          sortierung={{ spalte: 'suffix', richtung: 'auf' }}
          dicht
          suche={false}
          spaltenwahl={false}
          aktiveZeile={entwurf?.id}
          zeilenKlasse={(v) => (v.istAktiv ? undefined : 'schwach')}
          kontextmenue={[{ beschriftung: 'Bearbeiten …', beiWahl: bearbeiten }]}
          leer={<EmptyState klein titel="Für diese Gruppe ist keine Vorlage hinterlegt." />}
        />
      ))}

      {vorlagen.length === 0 && (
        <EmptyState
          titel="Für dieses Projekt ist keine Einstellwertvorlage hinterlegt."
          erklaerung={'Ohne Vorlage schlägt die Ableitung keinen Einstellwert vor — die '
            + 'Vorlagen kommen aus dem globalen Katalog nach Spezifikation 14.1.'}
        />
      )}

      {vorlagen.length > 0 && gruppen.length === 0 && (
        <EmptyState
          titel="Keine Vorlage passt zu dieser Nutzung."
          erklaerung={`${vorlagen.length} Vorlagen sind vorhanden.`}
          aktion={(
            <button type="button" className="m-knopf" onClick={() => setWerte({ nutzung: '' })}>
              Filter zurücksetzen
            </button>
          )}
        />
      )}

      <Drawer
        offen={entwurf !== null}
        titel={entwurf ? `${entwurf.suffix} — ${entwurf.bezeichnung}` : ''}
        beschreibung={entwurf
          ? `${entwurf.aggregatKuerzel ?? entwurf.anlagenart ?? '—'} · `
            + `Nutzung ${entwurf.nutzung ?? 'alle'}`
          : undefined}
        beiSchliessen={() => setEntwurf(null)}
        fuss={(
          <>
            <button type="button" className="m-knopf m-knopf-haupt" disabled={speichertLaeuft}
              onClick={() => void speichern()}>
              {speichertLaeuft ? 'speichert …' : 'Speichern'}
            </button>
            <button type="button" className="m-knopf" onClick={() => setEntwurf(null)}>
              Abbrechen
            </button>
          </>
        )}
      >
        {entwurf && !entwurf.projektbezogen && (
          <p className="hinweis warnung" style={{ marginTop: 0 }}>
            Diese Vorlage ist global — die Änderung wirkt für alle Projekte.
          </p>
        )}

        {entwurf && (
          <FormSection titel="Vorlage" spalten={2}>
            <Feld beschriftung="Suffix" wert={entwurf.suffix} pflicht
              beiAenderung={(w) => aendern({ suffix: w as string })} />
            <Feld beschriftung="Nutzung" wert={entwurf.nutzung ?? ''} platzhalter="alle"
              beiAenderung={(w) => aendern({ nutzung: (w as string) || undefined })} />
            <Feld beschriftung="Bezeichnung" wert={entwurf.bezeichnung} pflicht breite="voll"
              beiAenderung={(w) => aendern({ bezeichnung: w as string })} />
            <Feld beschriftung="Wert" wert={entwurf.wert} pflicht
              beiAenderung={(w) => aendern({ wert: w as string })} />
            <Feld beschriftung="Einheit" wert={entwurf.einheit ?? ''}
              beiAenderung={(w) => aendern({ einheit: (w as string) || undefined })} />
            <Feld art="zahl" beschriftung="Minimum" einheit={entwurf.einheit ?? undefined}
              wert={entwurf.min ?? null}
              beiAenderung={(w) => aendern({ min: (w as number | null) ?? undefined })} />
            <Feld art="zahl" beschriftung="Maximum" einheit={entwurf.einheit ?? undefined}
              wert={entwurf.max ?? null}
              beiAenderung={(w) => aendern({ max: (w as number | null) ?? undefined })} />
            <Feld art="zahl" beschriftung="Schrittweite" einheit={entwurf.einheit ?? undefined}
              wert={entwurf.schrittweite ?? null}
              beiAenderung={(w) => aendern({
                schrittweite: (w as number | null) ?? undefined,
              })} />
            <Feld beschriftung="Quelle" wert={entwurf.quelle ?? ''}
              beiAenderung={(w) => aendern({ quelle: (w as string) || undefined })} />
            <Feld art="schalter" beschriftung="Vorlage aktiv" wert={entwurf.istAktiv}
              beiAenderung={(w) => aendern({ istAktiv: w as boolean })} />
            <Feld beschriftung="Begründung" wert={entwurf.begruendung ?? ''} breite="voll"
              beiAenderung={(w) => aendern({ begruendung: (w as string) || undefined })} />
          </FormSection>
        )}

        {fehler && (
          <ErrorState
            klein
            meldung="Die Einstellwertvorlage konnte nicht gespeichert werden."
            technisch={fehler}
            beiErneutVersuchen={() => void speichern()}
          />
        )}
      </Drawer>
    </ContentSection>
  );
}
